import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { soccerPath, getLiveScrimmagePath, storeAuthRedirect } from '../utils/paths';
import './PracticeScrimmageStats.css';

const sortByGoalsThenName = (a, b) => {
  const gDiff = (b.goals + b.assists) - (a.goals + a.assists);
  if (gDiff !== 0) return gDiff;
  return (a.playerName || '').localeCompare(b.playerName || '', undefined, { sensitivity: 'base' });
};

const PublicPracticeScrimmageStats = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getPublicScrimmageStats(token);
      setStats(data);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load scrimmage stats');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (stats?.practiceId && !isAuthenticated) {
      storeAuthRedirect(getLiveScrimmagePath(stats.practiceId));
    }
  }, [stats?.practiceId, isAuthenticated]);

  const handleRecordLiveStats = () => {
    if (!stats?.practiceId) return;
    const livePath = getLiveScrimmagePath(stats.practiceId);
    if (isAuthenticated) {
      navigate(livePath);
      return;
    }
    storeAuthRedirect(livePath);
    navigate(soccerPath('/login'));
  };

  const topPerformers = useMemo(() => {
    if (!stats?.playerStats) return [];
    return [...stats.playerStats]
      .filter((p) => p.goals + p.assists + p.saves + p.defended > 0)
      .sort(sortByGoalsThenName)
      .slice(0, 8);
  }, [stats]);

  const handleShare = () => {
    if (!stats) return;
    const url = api.shareScrimmageStatsViaWhatsApp({
      title: stats.practiceTitle,
      practiceDate: stats.practiceDate,
      location: stats.location,
      shareToken: token,
      scoreRed: stats.scoreRed,
      scoreBlue: stats.scoreBlue,
      goals: stats.goals,
    });
    window.open(url, '_blank');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) return <div className="loading">Loading scrimmage stats…</div>;

  if (error) {
    return (
      <div className="scrimmage-stats scrimmage-stats--public">
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="scrimmage-stats scrimmage-stats--public">
        <div className="alert alert-warning">Stats not found</div>
      </div>
    );
  }

  const redStats = (stats.playerStats || []).filter((p) => p.teamSide === 'RED');
  const blueStats = (stats.playerStats || []).filter((p) => p.teamSide === 'BLUE');
  const otherStats = (stats.playerStats || []).filter((p) => !p.teamSide);

  const renderStatTable = (players, title, className) => {
    if (!players.length) return null;
    const sorted = [...players].sort((a, b) =>
      (a.playerName || '').localeCompare(b.playerName || '', undefined, { sensitivity: 'base' })
    );
    return (
      <section className={`scrimmage-section card ${className || ''}`}>
        <h2>{title}</h2>
        <div className="scrimmage-stats-table">
          <div className="scrimmage-stats-table__head">
            <span>Player</span>
            <span>G</span>
            <span>A</span>
            <span>SV</span>
            <span>Def</span>
          </div>
          {sorted.map((p) => (
            <div key={p.playerId} className="scrimmage-stats-table__row">
              <span>{p.playerName}</span>
              <span>{p.goals || '—'}</span>
              <span>{p.assists || '—'}</span>
              <span>{p.saves || '—'}</span>
              <span>{p.defended || '—'}</span>
            </div>
          ))}
        </div>
      </section>
    );
  };

  return (
    <div className="scrimmage-stats scrimmage-stats--public">
      <header className="scrimmage-public-header">
        <p className="scrimmage-public-header__club">{stats.clubName}</p>
        <h1>{stats.practiceTitle}</h1>
        {stats.practiceDate && <p>{formatDate(stats.practiceDate)}</p>}
        {stats.location && <p>📍 {stats.location}</p>}
        <div className="scrimmage-scoreboard scrimmage-scoreboard--public">
          <div className="scrimmage-scoreboard__team scrimmage-scoreboard__team--red">
            <span>🔴 Red</span>
            <strong>{stats.scoreRed ?? 0}</strong>
          </div>
          <span>–</span>
          <div className="scrimmage-scoreboard__team scrimmage-scoreboard__team--blue">
            <span>🔵 Blue</span>
            <strong>{stats.scoreBlue ?? 0}</strong>
          </div>
        </div>
        <button type="button" className="btn btn-whatsapp" onClick={handleShare}>
          📱 Share via WhatsApp
        </button>
        {stats.practiceId && (
          <button type="button" className="btn btn-primary scrimmage-public-record-btn" onClick={handleRecordLiveStats}>
            {isAuthenticated ? '📊 Update scrimmage stats' : '📊 Log in to update scrimmage stats'}
          </button>
        )}
      </header>

      {stats.goals?.length > 0 && (
        <section className="scrimmage-section card">
          <h2>Goals</h2>
          <ul className="scrimmage-goals-list">
            {[...stats.goals]
              .sort((a, b) => (a.minute ?? 0) - (b.minute ?? 0))
              .map((g) => (
                <li key={g.id} className="scrimmage-goal-item scrimmage-goal-item--readonly">
                  <span className="scrimmage-goal-item__minute">{g.minute}&apos;</span>
                  <span>{g.teamSide === 'RED' ? '🔴' : '🔵'}</span>
                  <span>
                    <strong>{g.scorerPlayerName}</strong>
                    {g.assistPlayerName && ` (assist: ${g.assistPlayerName})`}
                  </span>
                </li>
              ))}
          </ul>
        </section>
      )}

      {topPerformers.length > 0 && (
        <section className="scrimmage-section card">
          <h2>Top performers</h2>
          <ul className="scrimmage-top-list">
            {topPerformers.map((p) => (
              <li key={p.playerId}>
                <strong>{p.playerName}</strong>
                <span>
                  {[p.goals > 0 && `${p.goals} goal${p.goals > 1 ? 's' : ''}`,
                    p.assists > 0 && `${p.assists} assist${p.assists > 1 ? 's' : ''}`,
                    p.saves > 0 && `${p.saves} save${p.saves > 1 ? 's' : ''}`,
                    p.defended > 0 && `${p.defended} defended`]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="scrimmage-teams-grid">
        {renderStatTable(redStats, '🔴 Red team', 'scrimmage-section--red')}
        {renderStatTable(blueStats, '🔵 Blue team', 'scrimmage-section--blue')}
        {renderStatTable(otherStats, 'All players', '')}
      </div>
    </div>
  );
};

export default PublicPracticeScrimmageStats;
